import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useRegistration } from '../hooks/useRegistration'
import { PasswordInput, meetsPasswordRequirements } from './PasswordInput'
import { useUt } from '../../../context/UtFlagContext'
import { UtButton, UtField, UtToggle } from '../../../components/ut'

interface RegistrationFormProps {
  /** Path to redirect to after successful login (passed through to login page) */
  redirectTo?: string;
}

/**
 * Registration form component with email, friendly name, and password fields
 * Features password strength validation and matching confirmation
 */
const RegistrationForm: React.FC<RegistrationFormProps> = ({ redirectTo }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [friendlyName, setFriendlyName] = useState('')
  const [isWhatsAppMember, setIsWhatsAppMember] = useState(false)
  const [whatsAppNumber, setWhatsAppNumber] = useState('+44')
  
  const { handleRegistration, isLoading, validateUKMobileNumber } = useRegistration({ redirectTo })

  const passwordValid = meetsPasswordRequirements(password)
  const passwordsMatch = password === confirmPassword
  const { enabled: ut } = useUt()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await handleRegistration({
      email,
      password,
      confirmPassword,
      friendlyName,
      isWhatsAppMember,
      whatsAppNumber: isWhatsAppMember ? whatsAppNumber : undefined
    })
  }

  if (ut) {
    const numberOk = validateUKMobileNumber(whatsAppNumber)
    return (
      <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
        <UtField label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
        <UtField
          label="Friendly name"
          value={friendlyName}
          onChange={(e) => setFriendlyName(e.target.value)}
          placeholder="Your name on the teamsheet"
          hint="This name must be unique"
          required
        />
        <div className="ut-classic flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-ut-mut">Password</span>
          <PasswordInput value={password} onChange={setPassword} required />
        </div>
        <div className="ut-classic flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-ut-mut">Confirm password</span>
          <PasswordInput value={confirmPassword} onChange={setConfirmPassword} placeholder="Confirm password" isConfirm confirmValue={password} required />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-ut-line bg-ut-card px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold">WNF WhatsApp group member</span>
            <span className="text-xs text-ut-mut">Registration is only open to members of the WhatsApp group.</span>
          </div>
          <UtToggle checked={isWhatsAppMember} onChange={setIsWhatsAppMember} label="WNF WhatsApp group member" />
        </div>
        {isWhatsAppMember && (
          <UtField
            label="WhatsApp mobile number"
            type="tel"
            value={whatsAppNumber}
            onChange={(e) => setWhatsAppNumber(e.target.value)}
            placeholder="+44"
            hint="The number you use in the WNF WhatsApp group (format: +44XXXXXXXXXX)"
            error={whatsAppNumber && !numberOk ? 'Enter a UK mobile number starting +44' : undefined}
            required
          />
        )}
        <UtButton type="submit" size="lg" block disabled={!isWhatsAppMember || isLoading || !numberOk || !passwordValid || !passwordsMatch}>
          {isLoading ? 'CREATING ACCOUNT…' : 'CREATE ACCOUNT'}
        </UtButton>
        {password && !passwordValid && <span className="text-xs text-ut-bad">Your password doesn&apos;t meet all the requirements yet.</span>}
        <span className="text-sm text-ut-mut">
          Already have an account?{' '}
          <Link to="/login" state={{ from: redirectTo }} className="text-ut-acc hover:text-ut-ink">
            Sign in
          </Link>
        </span>
      </form>
    )
  }

  return (
    <>
      <h2 className="card-title justify-center mb-4">Create your WNF Account</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Email</legend>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="input w-full"
            required
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Friendly Name</legend>
          <input
            type="text"
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            placeholder="Your name on the teamsheet"
            className="input w-full"
            required
          />
          <p className="fieldset-label text-neutral-500">This name must be unique</p>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Password</legend>
          <PasswordInput
            value={password}
            onChange={setPassword}
            required
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Confirm Password</legend>
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirm Password"
            isConfirm
            confirmValue={password}
            required
          />
        </fieldset>

        <fieldset className="fieldset">
          <label className="flex items-center cursor-pointer justify-start gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              checked={isWhatsAppMember}
              onChange={(e) => setIsWhatsAppMember(e.target.checked)}
            />
            <span>WNF WhatsApp Group Member</span>
          </label>
          <p className="fieldset-label text-neutral-500">
            Currently, registration is only open to WNF WhatsApp Group members
          </p>
        </fieldset>

        {isWhatsAppMember && (
          <fieldset className="fieldset">
            <legend className="fieldset-legend">WhatsApp Mobile Number</legend>
            <input
              type="tel"
              value={whatsAppNumber}
              onChange={(e) => setWhatsAppNumber(e.target.value)}
              placeholder="+44"
              className={`input w-full ${
                whatsAppNumber && !validateUKMobileNumber(whatsAppNumber) ? 'input-error' : ''
              }`}
              required={isWhatsAppMember}
            />
            <p className="fieldset-label text-neutral-500">
              Enter the same mobile number you use for the WNF WhatsApp group (format: +44XXXXXXXXXX)
            </p>
          </fieldset>
        )}

        <div className="mt-6">
          {isWhatsAppMember ? (
            <>
              <button
                type="submit"
                className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                disabled={
                  isLoading ||
                  !validateUKMobileNumber(whatsAppNumber) ||
                  !passwordValid ||
                  !passwordsMatch
                }
              >
                Register
              </button>
              {password && !passwordValid && (
                <p className="mt-2 text-xs text-error">
                  Your password doesn't meet all the requirements yet
                </p>
              )}
            </>
          ) : (
            <div className="text-center text-error">
              Please confirm you are a WNF WhatsApp Group member to register
            </div>
          )}
        </div>

        <div className="divider">OR</div>

        <div className="text-center text-sm">
          Already have an account?{' '}
          <Link to="/login" state={{ from: redirectTo }} className="link link-primary">
            Login here
          </Link>
        </div>
      </form>
    </>
  )
}

export default RegistrationForm
